import { TFunction } from 'react-i18next';
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { formatDate } from "../../../helpers/dateUtils";
import { maybeT, formatValue } from "../../../helpers/helpers";
import { TransactionSummary } from "../../../models/transactions";
import { isValidValue } from "./printCommons";

const getPrintableSummary = (
    selectedRows: TransactionSummary[],
    options: {
        translate?: TFunction<"translation", undefined>;
        locale: string;
        hasModuleInvoiceNumber?: boolean;
        hasModuleTicketNumber?: boolean;
        hasPspReferencesNeededModules?: boolean;
        hasFeatureCCSurcharge?: boolean;
    } = { locale: "en" }
): jsPDF => {
    const { translate, locale, hasModuleInvoiceNumber, hasModuleTicketNumber, hasPspReferencesNeededModules, hasFeatureCCSurcharge } = options;
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({ orientation: "landscape" });

    if (!selectedRows || selectedRows.length === 0) return pdf;

    const cellValue = (val: any) => (val !== undefined && val !== null ? val : "-");

    // Strongly typed field config: [Label, translationKey, getter]
    const fieldConfig: [string, string, (r: any) => any][] = [
        ["Date", "date", r => formatDate(r?.date, locale)],
        ["Amount", "amount", r => formatValue(r?.amount, true, r?.currency, locale, undefined, true)],
        ["Tax", "tax", r => formatValue(r?.tax, true, r?.currency, locale, undefined, true)],
        ["Payment Method", "paymentMethod", r => r?.paymentMethod],
        ["Staff", "staff", r => r?.staff],
        ["Discount Amount", "discountAmount", r =>
            formatValue(r?.discountsSummary?.totalDiscountAmount, true, r?.currency, locale, undefined, true)],
        ["Gratuity", "gratuity", r => formatValue(r?.gratuity, true, r?.currency, locale, undefined, true)],
        ["Table Number", "tableNumber", r => r?.tableNumber],
        ["Items Sold", "itemsSold", r => r?.itemsSold],
        ["Location", "location", r => r?.location],
        ["Device", "device", r => r?.device],
        ["Customer", "customer", r => r?.customer],
        ["Barcode", "barcode", r => r?.barcode],
        ["Transaction ID", "transactionId", r => r?.transactionId],
    ];

    const firstRow = selectedRows[0];

    if (isValidValue(firstRow.loyaltyPointsDiscount))
        fieldConfig.push([
            "Loyalty Points",
            "loyaltyPoints",
            r => formatValue(r?.loyaltyPointsDiscount, true, r?.currency, locale, undefined, true),
        ]);

    if (hasPspReferencesNeededModules && isValidValue(firstRow.pspReferences?.length))
        fieldConfig.push(["PSP References", "pspReferences", r => r?.pspReferences?.join(", ")]);

    if (hasFeatureCCSurcharge && isValidValue(firstRow.surcharge?.total))
        fieldConfig.push([
            "Surcharge",
            "totalSurcharge",
            r => formatValue(r?.surcharge.total, true, r?.currency, locale, undefined, true),
        ]);

    if (hasModuleInvoiceNumber && isValidValue(firstRow.invoiceNumbers?.length))
        fieldConfig.push(["Invoice Numbers", "invoiceNumbers", r => r?.invoiceNumbers?.join(", ")]);

    if (hasModuleTicketNumber) fieldConfig.push(["Ticket Number", "ticketNumber", r => r?.ticketNumber]);

    if (isValidValue(firstRow.companyGuid))
        fieldConfig.push(["Company ID", "companyId", r => r?.companyGuid]);

    // Correct headers using translation keys
    const headers = fieldConfig.map(
        ([label, key]) =>
            maybeT(translate, `screens.transactions.fields.${key}`, label)
    );

    const body = selectedRows.map(row =>
        fieldConfig.map(([, , getter]) => cellValue(getter(row)))
    );

    // Compute equal widths so all columns fit the page
    const pageWidth = pdf.internal.pageSize.getWidth() - 20; // left+right margin
    const columnWidth = pageWidth / headers.length;

    const columnStyles: Record<number, any> = {};
    headers.forEach((_, i) => {
        columnStyles[i] = { cellWidth: columnWidth, halign: "left" };
    });

    pdf.setFontSize(10);

    autoTable(pdf, {
        head: [headers],
        body,
        startY: 15,
        theme: "grid",
        margin: { top: 10, left: 10, right: 10 },
        styles: { fontSize: 6 },
        columnStyles,
    });

    return pdf;
};

export default getPrintableSummary;
